import PropTypes from 'prop-types';
import {useCallback, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {
  Box, Button, Chip, Dialog, DialogContent, IconButton, Stack, Typography,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import Page from '../Page';
import useAuth from '../../hooks/useAuth';
import {isPaidFeatureAvailable} from '../../utils/planEntitlements';
import {PATH_PAGE} from '../../routes/paths';

const FEATURES = {
  enrichment: {
    name: 'Enriquecimento de contatos',
    title: 'Transforme nomes em novas oportunidades',
    description: 'Encontre mais e-mails e telefones dos decisores e dê o próximo passo na sua prospecção.',
    plan: 'Disponível a partir do plano Starter',
    Icon: PersonOutlineIcon,
    benefits: ['Busque novos e-mails e telefones dos decisores', 'Enriqueça contatos individuais ou toda a campanha', 'Receba os resultados diretamente no seu CRM'],
    note: 'O enriquecimento utiliza 3 créditos por contato. Uma assinatura ativa é necessária, mesmo com créditos disponíveis.',
  },
  mailbox: {
    name: 'Caixa de e-mail',
    title: 'Cada resposta, uma conversa mais perto da venda',
    description: 'Conecte suas contas de e-mail e acompanhe as conversas com seus leads em um só lugar.',
    plan: 'Disponível nos planos Growth, Pro e Enterprise',
    Icon: MailOutlineIcon,
    benefits: ['Conecte Google, Microsoft ou seu e-mail corporativo', 'Centralize mensagens enviadas e respostas recebidas', 'Responda aos leads sem sair da 1Lead'],
    note: 'Créditos avulsos não liberam a caixa de e-mail nem a conexão de contas. Escolha um plano compatível para começar.',
  },
  whatsapp: {
    name: 'WhatsApp',
    title: 'Aproxime sua equipe de cada oportunidade',
    description: 'Leve as conversas com seus leads para o WhatsApp e mantenha o contexto da prospecção na 1Lead.',
    plan: 'Disponível nos planos Pro e Enterprise',
    Icon: WhatsAppIcon,
    benefits: ['Conecte suas contas de WhatsApp', 'Converse com leads em uma caixa de entrada integrada', 'Acompanhe o histórico e os dados do contato'],
    note: 'Créditos avulsos não liberam o WhatsApp. Escolha um plano compatível para conectar suas contas e conversar com leads.',
  },
};

export function PaidFeatureOffer({feature, onClose}) {
  const offer = FEATURES[feature];
  const OfferIcon = offer.Icon;
  return (
    <Box sx={{p: {xs: 3, sm: 4}, position: 'relative'}}>
      {onClose && <IconButton aria-label="Fechar" onClick={onClose} sx={{position: 'absolute', top: 12, right: 12}}><CloseIcon/></IconButton>}
      <Stack spacing={2.5}>
        <Box sx={{width: 56, height: 56, borderRadius: 2, display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)}}>
          <OfferIcon style={{fontSize: 30}}/>
        </Box>
        <Box>
          <Chip size="small" icon={<LockOutlinedIcon style={{fontSize: 16}}/>} label="Exclusivo para assinantes" variant="outlined" sx={{mb: 2}}/>
          <Typography variant="overline" component="p" color="primary.main">{offer.name}</Typography>
          <Typography id={`paid-feature-${feature}-title`} component="h2" variant="h4" sx={{mt: 0.5, pr: 1}}>{offer.title}</Typography>
          <Typography color="text.secondary" sx={{mt: 1}}>{offer.description}</Typography>
        </Box>
        <Stack component="ul" spacing={1.5} sx={{m: 0, p: 0, listStyle: 'none'}}>
          {offer.benefits.map((benefit) => <Stack component="li" key={benefit} direction="row" spacing={1.25} alignItems="flex-start">
            <Box component={CheckCircleIcon} sx={{color: 'primary.main', fontSize: 20, flexShrink: 0, mt: 0.25}}/>
            <Typography variant="body2">{benefit}</Typography>
          </Stack>)}
        </Stack>
        <Box sx={{p: 2, borderRadius: 1.5, bgcolor: 'background.neutral'}}>
          <Typography variant="subtitle2">{offer.plan}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{offer.note}</Typography>
        </Box>
        <Button component={RouterLink} to={PATH_PAGE.pricing} onClick={onClose} variant="contained" size="large" endIcon={<ArrowForwardIcon/>}>Ver planos e fazer upgrade</Button>
        {onClose && <Button onClick={onClose} color="inherit" sx={{alignSelf: 'center'}}>Agora não</Button>}
      </Stack>
    </Box>
  );
}

PaidFeatureOffer.propTypes = {feature: PropTypes.oneOf(Object.keys(FEATURES)).isRequired, onClose: PropTypes.func};

export function PaidFeatureUpgradeDialog({feature, open, onClose}) {
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={`paid-feature-${feature}-title`}>
    <DialogContent sx={{p: 0}}><PaidFeatureOffer feature={feature} onClose={onClose}/></DialogContent>
  </Dialog>;
}
PaidFeatureUpgradeDialog.propTypes = {...PaidFeatureOffer.propTypes, open: PropTypes.bool.isRequired};

export function usePaidFeature(feature) {
  const {planEntitlements} = useAuth();
  const available = isPaidFeatureAvailable(planEntitlements, feature);
  const [open, setOpen] = useState(false);
  const requireAccess = useCallback(() => {
    if (available) return true;
    setOpen(true);
    return false;
  }, [available]);
  const handleAccessError = useCallback((error) => {
    if (!['PAID_SUBSCRIPTION_REQUIRED', 'PLAN_FEATURE_NOT_AVAILABLE'].includes(error?.code)) return false;
    setOpen(true);
    return true;
  }, []);
  return {available, requireAccess, handleAccessError, upgradeDialog: <PaidFeatureUpgradeDialog feature={feature} open={open} onClose={() => setOpen(false)}/>};
}

export default function PaidFeatureGate({feature, children}) {
  const {planEntitlements} = useAuth();
  if (isPaidFeatureAvailable(planEntitlements, feature)) return children;
  return <Page title={FEATURES[feature].name}><Box sx={{maxWidth: 640, mx: {xs: 2, sm: 'auto'}, my: {xs: 2, md: 5}, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper'}}>
    <PaidFeatureOffer feature={feature}/>
  </Box></Page>;
}
PaidFeatureGate.propTypes = {feature: PaidFeatureOffer.propTypes.feature, children: PropTypes.node};
